import { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import AppointmentApiInvoker from '../../../../api/AppointmentApiInvoker';
import memory from '../../../../config/memory.js';
import {
  Customer,
  Hospital,
  DialogTraslado3State,
  TempService,
  TransferType,
  Driver
} from './types';
import { useTranslation } from 'react-i18next';
import TrasladoPDF from '../Documents/TrasladoPDF/TrasladoPDF.js';
import Authority from '../../../../util/Authority';
import ApiInvoker from '../../../../api/ApiInvoker';
import { reverseGeocode } from '../../../../util/geoLocalization/reverseGeocode';
import { PatchBookingBody, patchTransferBooking } from '../../../../api/Mobility/PatchTransferBookings/patchTransferBookings';

interface MemoryConfig {
  tempService: TempService | null;
  nosocomios: Hospital[];
  traslados: TransferType[];
}

const typedMemory = memory as unknown as MemoryConfig;

interface UseDialogTrasladoProps {
  customer?: Customer;
  appointment?: any;
  event?: any;
  viewMode?: 'short' | 'large' | 'edit' | 'assign_agenda' | 'apointment';
  disabled?: boolean;
  editData?: any[];
  item?: any;
  itemSelect?: any;
  provider?: any;
  hospitals?: Hospital[];
  onCloseModal?: () => void;
  handleChangeValue?: (key: string, value: any) => void;
  openDrivers?: () => void;
  saveService?: () => void;
  drivers?: Driver[];
}

export const useDialogTraslado = (props: UseDialogTrasladoProps) => {
  const { t } = useTranslation();
  const [isReady, setIsReady] = useState(false);

  const directionAttemptsRef = useRef(0);
  const transferWay = props.event?.origin?.transferItemRequestId?.transferWay;  

  const [state, setState] = useState<DialogTraslado3State>({
    open: false,   
    types: [
      { value: "Practice", id: 1 },
      { id: 2, value: "Transfer" },
    ],
    nosocomios: typedMemory.nosocomios,
    traslados: typedMemory.traslados,
    type: "",
    module: "",
    component: null,
    current: null,
    action: "",
    address: "",
    traslado: 0,
    nosocomio: 0,
    appointmentAction: "",
    time_end_suggested: "-",
    distance_suggested: "-",
    waypoints: [],
    nosocomioObject: null,
    directions: [],
    recurrence: {
      recurrenceType: "",
      every: 0,
      weekDays: [],
      quantityIntraday: 0,
      finish: "",
      finishOcurrences: 0,
      finishDate: "",
      monthRecurrence: [],
      endType: ""
    },
    hospitals: [],
    startTime: "02:00",
    startDate: "",
    rawPath: null,
    path1: [],
    path2: [],
    indexPosition: 0,
    currentPosition: null,
    pathExtra: null,
    time_recorrend: 720,
    distance_recorred: 5100,
    time_end: 0,
    driver: undefined
  });

  const [canSubmit, setCanSubmit] = useState(false);
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<any[] | null>(null);

  const formatTransferTypeSend = (type: string): number => {
    if (type === "Traslado estándar") return 1;
    if (type === "Traslado con rampa") return 2;
    if (type === "Traslado con ambulancia") return 3;
    return 0;
  };

  const setDataToEdit = (data: any[]) => {
    if (props.item) {
      const selectedItem = data[0]?.recurrence?.items?.find((service: any) => service.order === props.item);
      if (!selectedItem) {
        setIsReady(true);
        return;
      }

      const formattedRecurrence = {
        recurrenceType: selectedItem.recurrence.recurrenceType,
        every: selectedItem.recurrence.every,
        weekDays: selectedItem.recurrence.weekDays,
        quantityIntraday: selectedItem.recurrence.quantityIntraday,
        finish: selectedItem.recurrence.finish,
        finishOcurrences: selectedItem.recurrence.finishOcurrences,
        finishDate: selectedItem.recurrence.finishDate,
        monthRecurrence: selectedItem.recurrence.monthRecurrence,
        endType: selectedItem.recurrence.endType,
      };

      AppointmentApiInvoker.getHospital((response: any) => {
        const hospital = response.filter((id: any) => id.hospitalId === selectedItem.hospital.hospitalId);
        if (!hospital[0]) {
          setIsReady(true);
          return;
        }

        const scheduleDate = props.event?.origin?.transferScheduleDateTime 
          ? moment(props.event.origin.transferScheduleDateTime).format('YYYY-MM-DD')
          : selectedItem?.startDate;

        setState((prevState: DialogTraslado3State) => {
          const newState = {
            ...prevState,
            traslado: formatTransferTypeSend(data[0].serviceName),
            nosocomio: selectedItem?.hospital?.hospitalId,
            nosocomioObject: hospital[0],
            startDate: scheduleDate,
            startTime: selectedItem?.startTime,
            recurrence: formattedRecurrence,
            type: data[0]?.type ?? prevState.type,
            address: selectedItem?.hospital?.address ?? prevState.address,
          };
          return newState;
        });

        setIsReady(true);
      });
    } else if (props.appointment) {
      const appt = props.appointment;
      
      const scheduleDate = props.event?.origin?.transferScheduleDateTime 
        ? moment(props.event.origin.transferScheduleDateTime).format('YYYY-MM-DD')
        : appt?.startDate;

      setState((prevState: DialogTraslado3State) => {
        const newState = {
          ...prevState,
          traslado: appt?.traslado ?? prevState.traslado,
          nosocomio: appt?.nosocomio ?? prevState.nosocomio,
          nosocomioObject: appt?.nosocomioObject?? prevState.nosocomioObject,
          startDate: scheduleDate ?? prevState.startDate,
          startTime: appt?.startTime ?? prevState.startTime,
          recurrence: appt?.recurrence ?? prevState.recurrence,
          type: appt?.type ?? prevState.type,
          address: appt?.address ?? prevState.address,
        };
        return newState;
      });
      setIsReady(true);
    } else {
      setIsReady(true);
    }
  };

  const getHospital = async () => {
    try {
      const response = await AppointmentApiInvoker.getHospital();
      if (Array.isArray(response)) {
        const formattedHospitals = formatHospital(response);
        setState((prevState: DialogTraslado3State) => ({
          ...prevState,
          hospitals: formattedHospitals
        }));
      }
    } catch (error) {
      console.error('Error fetching hospitals:', error);
    }
  };

  const formatHospital = (data: any[]): Hospital[] => {
    return data.map((c) => ({
      id: c.hospitalId ?? '',
      latitude: c.latitude ?? 0,
      value: c.businessName ?? '',
      address: c.address ?? '',
      longitude: c.longitude ?? 0,
    }));
  };

 

  const formatTransferTypeRecive = (type: string | undefined) => {
    if (!type) return "-";
    if (type === "STANDAR") return t("Traslado estándar");
    if (type === "RAMPA") return t("Traslado con rampa");
    if (type === "AMBULANCIA") return t("Traslado con ambulancia");
    return type;
  };

  const formatDistance = (value: number | undefined, isRealDistance: boolean = false) => {
    if (typeof value !== 'number' || isNaN(value)) {
      return '-';
    }

    const absoluteValue = Math.abs(value);
    
    if (isRealDistance && (props.appointment?.appointmentStatus === "ENDED" || getStatus() === "DONE")) {
      if (absoluteValue < 1000) {
        return t('common.mettersValue', { value: Math.round(absoluteValue) });
      }
      const kilometers = (absoluteValue / 1000).toFixed(1);
      return t('common.kilometersValue', { value: kilometers });
    }
    
    if (absoluteValue < 1) {
      return t('common.mettersValue', { value: Math.round(absoluteValue * 1000) });
    }
    return t('common.kilometersValue', { value: absoluteValue.toFixed(1) });
  };

  const translateStatus = (status: string | undefined) => {
    if (!status) return "-";
    return t(`status.${status}`)
  };

  const formatPatientAddress = (customer: any) => {
    if (!customer) return "-";
    
    const schedule = props.appointment?.schedules?.find(
      (schedule: any) => schedule.transferScheduleId === props.event?.id
    );

    let addressData = undefined
    if (!schedule) {
      addressData = props.event.origin.transferWay === "RETURNTRIP" ? {address:`${props.event?.origin?.transferItemRequestId?.hospital?.businessName} - ${props.event?.origin?.transferItemRequestId?.hospital?.address}`} : {address: `${props.event?.origin?.address?.street} ${props.event?.origin?.address?.number}`}
    }else{
      addressData = schedule.transferWay === 'RETURNTRIP' ? schedule.destination : schedule.origin;
    }
    
    if (!addressData) return "-";

    const address = addressData.address;
    const location = addressData?.locationName  ?? ""
    const province = addressData?.provinceName ?? ""
    
    const addressParts = [
      address,
      location,
      province
    ].filter(part => part); 
    
    return addressParts.length > 0 ? addressParts.join(", ") : "-";
  };

  const canAssignDriver = () => {
    if (!props.editData) return false;
    const i = props.editData.findIndex((obj) => obj.id === props.itemSelect);
    if (i !== -1) {
      let userCompanyId;
      if (Authority.isFinancierUser()) {
        userCompanyId = localStorage.getItem('itlg_default_company_id');
      } else if (Authority.isProviderUser()) {
        userCompanyId = localStorage.getItem('company_partnership_id');
      }
      return Number(userCompanyId) === props.editData[i]?.providerId;
    } else {
      return false;
    }
  };

  const validStatus = ["TRANSFER_SCHEDULES_ASSIGNED", "ENDED", "PARTIAL_ASSIGNED"]

  const getStatus = () => {
    return validStatus.includes(props.appointment?.appointmentStatus)
      ? props?.event?.origin?.transferScheduleStatus
      : undefined;
  };

  const handleToPDF = async () => {
    const appointment = props.appointment;
    const event = props.event;
    const transferSchedule = appointment?.schedules?.find(
      (schedule: any) => schedule.transferScheduleId === event?.id
    );
    const transferType = transferSchedule?.transferItemRequestId?.transferType;
    const geoTrackDistance = transferSchedule?.geoTrackDistance;
    const formattedDistance = geoTrackDistance ? formatDistance(geoTrackDistance, true) : t("mobility.no.distance");

    const duration = transferSchedule?.startDateTime && transferSchedule?.endDateTime 
      ? (() => {
          const diff = moment.duration(moment(transferSchedule.endDateTime).diff(moment(transferSchedule.startDateTime)));
          const hours = Math.floor(diff.asHours());
          const minutes = diff.minutes();
          const seconds = diff.seconds();
          return `${hours} ${t("common.hoursShort")}, ${minutes} ${t("common.minutesShort")}, ${seconds} ${t("common.secondsShort")}`;
        })()
      : "-";

    let realStartLocation = null;
    let realEndLocation = null;

    try {
      if (transferSchedule?.startLatitude && transferSchedule?.startLongitude) {

        realStartLocation = await reverseGeocode(t, transferSchedule.startLatitude, transferSchedule.startLongitude);
      }

      if (transferSchedule?.endLatitude && transferSchedule?.endLongitude) {

        realEndLocation = await reverseGeocode(t, transferSchedule.endLatitude, transferSchedule.endLongitude);
      }
    } catch (error) {
      console.error('[handleToPDF] Error getting real locations:', error);
    }

    const context = {
      appointmentId: event?.id,
      startDate: moment(new Date(event?.start)).format("DD/MM/YYYY"),
      startTime: moment(transferSchedule?.startDateTime).format("HH:mm"),
      realDate: moment(new Date(event?.start)).format("DD/MM/YYYY"),
      realTime: moment(transferSchedule?.endDateTime).format("HH:mm"),
      address_fin: transferSchedule?.transferItemRequestId?.hospital?.address,
      trasladoName: t(`status.transfer.${transferType}`),
      financier: {
        financier: appointment?.financier?.financiers?.[0]?.financier?.businessName,
        provider: appointment?.schedules?.[0]?.transferItemRequestId?.companyIdPartnership?.name
      },
      customer: {
        firstName: appointment?.customer?.name ?? '-',
        lastName: appointment?.customer?.lastname ?? '-',
        address: transferSchedule?.origin?.address ?? '-',
        location: transferSchedule?.origin?.locationName ?? '-',
        province: transferSchedule?.origin?.provinceName ?? '-',
        typeDocument: appointment?.customer?.typeDocument ?? '-',
        document: appointment?.customer?.identity ?? '-',
        realStartLocation: realStartLocation?.address ?? '-',
        realStartLocationLocalidad: realStartLocation?.location ?? '-',
        realStartLocationProvincia: realStartLocation?.province ?? '-',
      },
      driver: {
        firstName: transferSchedule?.driver?.firstName ?? '-',
        lastName: transferSchedule?.driver?.lastName ?? '-',
        typeDocument: transferSchedule?.driver?.idType?.name ?? '-',
        document: transferSchedule?.driver?.id ?? '-',
      },
      nosocomio: {
        address: transferSchedule?.destination?.address ?? '-',
        location: transferSchedule?.destination?.locationName ?? '-',
        province: transferSchedule?.destination?.provinceName ?? '-',
        realEndLocation: realEndLocation?.address ?? '-',
        realEndLocationLocalidad: realEndLocation?.location ?? '-',
        realEndLocationProvincia: realEndLocation?.province ?? '-',
      },
      trayecto: {
        distance: formattedDistance,
        duration: duration,
        coordOmission: transferSchedule?.coordOmission ?? false
      },
      inicioCertificado: transferSchedule?.startCertified,
    };

    TrasladoPDF(context, t);
  };

  const getTransferWayLabel = () => {
    const transferWay = props.event?.origin?.transferWay;
    if(transferWay) {
      if (transferWay === 'ONEWAY') return t('service.oneWay.label');
      if (transferWay === 'RETURNTRIP') return t('service.returnTrip.label');
    }else {
      const transferWay = props.event?.origin?.transferItemRequestId?.transferWay;
      if (transferWay === 'ONEWAY') return t('service.oneWay.label');
      if (transferWay === 'RETURNTRIP') return t('service.returnTrip.label');
    }
    return '';
  };

  const isOneWay = props.event?.origin?.transferWay === 'ONEWAY' || props.event?.origin?.transferItemRequestId?.transferWay === 'ONEWAY';

  const handleVerify = (formData: any, transferScheduleId: number) => {
    setIsLoading(true);
    setErrors(null);
    ApiInvoker.mobilityTransferEdit(
      transferScheduleId,
      formData,
      true, // only-check
      (response: any) => {
        setIsLoading(false);
        if (response.status === 'OK') {
          setChecked(true);
          setCanSubmit(true);
        } else {
          setChecked(false);
          setIsLoading(false);
          setCanSubmit(false);
          setErrors(response.errors);
        }
      },
      (err: any) => {
        setChecked(false);
        setCanSubmit(false);
        setErrors([{ detail: 'Error de red o servidor' }]);
      }
    );
  };

  const handleSubmit = async(formData: any, transferScheduleId: number, onSuccess?: (data?:any) => void, isBookingUpdate?: boolean) => {
  
    if(isBookingUpdate){
      const {driver, scheduleTransferDateTime} = formData
    const parsedObj: PatchBookingBody = {
         driverId: driver.personId,
        bookingStartDateTime: scheduleTransferDateTime
    }
    try {
      await patchTransferBooking({bookingId: transferScheduleId, body: parsedObj, callback: (data:string) => {
        setErrors(null);
        if(onSuccess)  onSuccess()

      }, callerror: (error:string) => {setErrors([error])}})
      
    } catch (error) {
        console.error('Error de red o servidor:', error);
        setErrors([{ detail: 'Error de red o servidor' }]);
    }
   
    
    }else{
      ApiInvoker.mobilityTransferEdit(
        transferScheduleId,
        formData,
        false, // only-check
        (response: any) => {
          if (response.status === 'OK') {
            setErrors(null);
            if (onSuccess) onSuccess();
          } else {
            setErrors(response.errors);
          }
        },
        (err: any) => {
          console.error('Error de red o servidor:', err);
          setErrors([{ detail: 'Error de red o servidor' }]);
        }
      );
    }
    


  };

  const handleValue = useCallback((key: string, value: any) => {
    setCanSubmit(false);
    setErrors(null);
    if (key === "nosocomio") {
      const moso = props.hospitals?.filter((mo) => mo.id == value) || [];
      if (moso.length > 0) {
        setState((prevState: DialogTraslado3State) => ({
          ...prevState,
          nosocomioObject: moso[0]
        }));
      }
    }
    setState((prevState: DialogTraslado3State) => {
      const newState = {
        ...prevState,
        [key]: value
      };
      return newState;
    });
    if (props.handleChangeValue) {
      props.handleChangeValue(key, value);
    }
  }, [props.hospitals, props.handleChangeValue]);

  const handleTimeChange = useCallback((value: string, name?: string) => {
    if (!value) return;
    const parsedTime = moment(value, ["HH:mm", "hh:mm a"]).format("HH:mm");
    
    const duration = props.event?.origin?.estimatedDuration ?? props.event?.origin?.transferItemRequestId?.duration ?? moment(props.event.origin.endDateTime).diff(props.event.origin.startDateTime, "seconds")
    if (name === 'edit_arrival_time') {
      const arrivalTime = moment(parsedTime, "HH:mm");
      const pickupTime = moment(arrivalTime).subtract(duration, "seconds");
      
      setState((prevState: DialogTraslado3State) => ({
        ...prevState,
        startTime: pickupTime.format("HH:mm"),
        time_end_suggested: parsedTime
      }));
      
      if (props.handleChangeValue) {
        props.handleChangeValue("startTime", pickupTime.format("HH:mm"));
      }
    } else {
      const pickupTime = moment(parsedTime, "HH:mm");
      const arrivalTime = moment(pickupTime).add(duration, "seconds");
      
      setState((prevState: DialogTraslado3State) => ({
        ...prevState,
        startTime: parsedTime,
        time_end_suggested: arrivalTime.format("HH:mm")
      }));
      
      if (props.handleChangeValue) {
        props.handleChangeValue("startTime", parsedTime);
      }
    }
  }, [props.event, props.handleChangeValue]);

  useEffect(() => {
    const estimated = props.event?.origin?.estimatedDuration;
    const fallback = props.event?.origin?.transferItemRequestId?.duration;
    
    if (estimated || fallback) {
      const newTimeEnd = estimated ?? fallback ?? 960;
      
      let startTime = null;
      if (props.event?.transferScheduleDateTime) {
        startTime = moment(props.event.transferScheduleDateTime).format('HH:mm');
      } else if (props.event?.origin?.transferScheduleDateTime) {
        startTime = moment(props.event.origin.transferScheduleDateTime).format('HH:mm');
      } else if (props.event?.startDateTime) {
        startTime = moment(props.event.startDateTime).format('HH:mm');
      } else if (props.event?.origin?.startDateTime) {
        startTime = moment(props.event.origin.startDateTime).format('HH:mm');
      }

      let endTime = null;
      if (props.event?.transferScheduleEndDateTime) {
        endTime = moment(props.event.transferScheduleEndDateTime).format('HH:mm');
      } else if (props.event?.origin?.transferScheduleEndDateTime) {
        endTime = moment(props.event.origin.transferScheduleEndDateTime).format('HH:mm');
      } else if (props.event?.origin?.endDateTime) {
        endTime = moment(props.event.origin.endDateTime).format('HH:mm');
      } else if (props.event?.endDateTime) {
        endTime = moment(props.event.endDateTime).format('HH:mm');
      }

      let startDate = null;
      if (props.event?.transferScheduleDateTime) {
        startDate = moment(props.event.transferScheduleDateTime).format('YYYY-MM-DD');
      } else if (props.event?.origin?.transferScheduleDateTime) {
        startDate = moment(props.event.origin.transferScheduleDateTime).format('YYYY-MM-DD');
      } else if (props.event?.startDateTime) {
        startDate = moment(props.event.startDateTime).format('YYYY-MM-DD');
      } else if (props.event?.origin?.startDateTime) {
        startDate = moment(props.event.origin.startDateTime).format('YYYY-MM-DD');
      }

      setState(prevState => ({
        ...prevState,
        time_end: newTimeEnd,
        startTime: startTime ?? prevState.startTime,
        time_end_suggested: endTime ?? prevState.time_end_suggested,
        startDate: startDate ?? prevState.startDate
      }));
    }
  }, [props.event]);

  useEffect(() => {
    if (props.viewMode === 'edit' && props.event) {
      const estimated = props.event?.origin?.estimatedDuration;
      const fallback = props.event?.origin?.transferItemRequestId?.duration;
      const duration = estimated ?? fallback ?? 960;

      let startTime = null;
      if (props.event?.startDateTime) {
        startTime = moment(props.event.startDateTime).format('HH:mm');
      } else if (props.event?.origin?.startDateTime) {
        startTime = moment(props.event.origin.startDateTime).format('HH:mm');
      }

      let endTime = null;
      if (props.event?.origin?.endDateTime) {
        endTime = moment(props.event.origin.endDateTime).format('HH:mm');
      } else if (props.event?.transferScheduleEndDateTime) {
        endTime = moment(props.event.transferScheduleEndDateTime).format('HH:mm');
      }

      let startDate = null;
      if (props.event?.origin?.transferScheduleDateTime) {
        startDate = moment(props.event.origin.transferScheduleDateTime).format('YYYY-MM-DD');
      } else if (props.event?.transferScheduleDateTime) {
        startDate = moment(props.event.transferScheduleDateTime).format('YYYY-MM-DD');
      } else if (props.event?.startDateTime) {
        startDate = moment(props.event.startDateTime).format('YYYY-MM-DD');
      } else if (props.event?.origin?.startDateTime) {
        startDate = moment(props.event.origin.startDateTime).format('YYYY-MM-DD');
      }

      setState(prevState => {
        const newState = {
          ...prevState,
          time_end: duration,
          startTime: startTime ?? prevState.startTime,
          time_end_suggested: endTime ?? prevState.time_end_suggested,
          startDate: startDate ?? prevState.startDate,
          driver: props.event?.origin?.driver?.personId ?? null,
          traslado: props.event?.origin?.transferItemRequestId?.transferType ? formatTransferTypeSend(props.event.origin.transferItemRequestId.transferType) : prevState.traslado,
          nosocomio: props.event?.origin?.transferItemRequestId?.hospital?.hospitalId ?? prevState.nosocomio
        };
        return newState;
      });
    }
  }, [props.viewMode, props.event]);

  useEffect(() => {
    setIsReady(false);

    directionAttemptsRef.current = 0;
    getHospital();
    
    if ((props.editData && props.editData.length > 0) || props.item) {
      setDataToEdit(props.editData ?? []);
    } else if (props.appointment) {
      setDataToEdit([]);
    } else {
      setIsReady(true);
    }

    if (typedMemory.tempService?.type) {
      const tempService = typedMemory.tempService;
      setState((prevState: DialogTraslado3State) => ({
        ...prevState,
        type: tempService.type,
        startTime: tempService.startTime,
        startDate: tempService.startDate,
        recurrence: tempService.recurrence,
        nosocomios: props.hospitals ?? [],
      }));
      handleValue("traslado", tempService.traslado.id);
      handleValue("nosocomio", tempService.nosocomio.id);
    } 

    if (props.viewMode === 'edit') {
      const driver = props.event?.origin?.driver ?? props.event?.origin?.transferItemRequestId?.driver;
      if (driver?.personId) {
        handleValue("driver", driver.personId);
      }
    }
  }, []);

  const VALID_EDIT_STATUSES = ['PENDING_APPROVAL', 'APPROVED', 'REJECTED'] as const;

  const hasPermissionToEditEvent = (): boolean => 
    Authority.isFinancierUser() || 
    props.event?.origin?.transferItemRequestId?.companyIdPartnership?.companyId === Authority.getAuthorizedCompanyId();

  const isEditable = (): boolean => {
    if (props.event?.assignAgendaMode) {
      return true;
    }

    const status = getStatus();
    return VALID_EDIT_STATUSES.includes(status as typeof VALID_EDIT_STATUSES[number]) && 
           hasPermissionToEditEvent();
  };





  return {
    state,
    isReady,
    handleValue,
    formatTransferTypeRecive,
    formatDistance,
    translateStatus,
    formatPatientAddress,
    canAssignDriver,
    getStatus,
    handleToPDF,
    getTransferWayLabel,
    transferWay,
    handleTimeChange,
    isEditable,
    isOneWay,
    canSubmit,
    checked,
    isLoading,
    errors,
    handleVerify,
    handleSubmit
  };
}; 